import React, { useCallback, useEffect, useState } from 'react';

import { MenuItem, Table } from 'components';
import { DocumentText, InfoCircle } from 'iconsax-react';

import { AuditDetailModal } from './AuditDetailModal';
import { auditColumns } from './auditColumns';

import { useSelector, useTranslation } from 'third-party';

import {
  DEFAULT_TIMEZONE,
  PersisTableFilters,
  PersisTableSort,
  PersisTables,
} from 'constants/common';

import { useAudit } from 'modules/audit';

import { appSelectors, auditSelectors, settingSelectors } from 'selectors';

import { FilterValue } from 'components/Table/types';

import {
  extractFiltersFromSavedColumns,
  mergeSavedColumnsWithExisting,
} from 'utils/app';
import {
  getFromStorage,
  removeFromStorage,
  saveInStorage,
} from 'utils/storage';

import { APIAudit, APISortOrder } from 'types/api';
import { APISettingName } from 'types/backend';

import { palette } from 'styles/theme';

const DEFAULT_AUDIT_SORT = {
  sortField: 'createdAt',
  sortOrder: APISortOrder.DESC,
};

export const AuditsList = () => {
  const { t } = useTranslation();
  const timezoneValue = useSelector(
    settingSelectors.getSettingValue(APISettingName.Timezone, DEFAULT_TIMEZONE),
  );
  const [columns, setColumns] = useState(auditColumns(timezoneValue));
  const [selectedAudit, setSelectedAudit] = useState<APIAudit | null>(null);

  const { loadAudits, loadMoreAudits, updateAuditFilters, clearAuditList } =
    useAudit();

  const [loadedUserSettings, setLoadedUserSettings] = useState(false);
  const [isFilteringOn, setIsFilteringOn] = useState(false);
  const organizationId = useSelector(appSelectors.organizationId);
  const items = useSelector(auditSelectors.auditList);
  const auditsListLoaded = useSelector(auditSelectors.auditsListLoaded);
  const allAuditLoaded = useSelector(auditSelectors.allAuditLoaded);
  const auditFilters = useSelector(auditSelectors.auditFilters);
  const loadAuditsPending = useSelector(auditSelectors.loadAuditPending);
  const loadMoreAuditsPending = useSelector(
    auditSelectors.loadMoreAuditPending,
  );

  useEffect(() => {
    if (organizationId && loadedUserSettings) {
      loadAudits();
    }
  }, [organizationId, auditFilters, loadedUserSettings, loadAudits]);

  useEffect(() => {
    const savedColumns = getFromStorage(
      PersisTables.Audits,
      auditColumns(timezoneValue),
    );
    const savedSort = getFromStorage(
      PersisTableSort.Audits,
      DEFAULT_AUDIT_SORT,
    );

    const columnFilters = savedColumns
      ? extractFiltersFromSavedColumns(savedColumns)
      : {};

    updateAuditFilters({ ...columnFilters, searchSortOrders: [savedSort] });

    if (savedColumns) {
      setColumns(mergeSavedColumnsWithExisting(savedColumns, columns));
    }
    setIsFilteringOn(getFromStorage(PersisTableFilters.Audits, false));
    setLoadedUserSettings(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  useEffect(() => {
    return () => {
      clearAuditList();
    };
  }, [clearAuditList]);

  const onAdvancedFilterChange = useCallback(
    (columnId: string, value: FilterValue) => {
      setColumns(
        columns.map(column => {
          if (column.dataId === columnId) {
            return { ...column, filterValue: value };
          }
          return column;
        }),
      );

      updateAuditFilters({ ...auditFilters, [columnId]: value });
    },
    [auditFilters, columns, updateAuditFilters],
  );

  const onColumnFilterChange = useCallback(
    (columnId: string, value: string) => {
      setColumns(
        columns.map(column => {
          if (column.dataId === columnId) {
            return { ...column, filter: value };
          }
          return column;
        }),
      );

      updateAuditFilters({ ...auditFilters, [columnId]: value });
    },
    [auditFilters, columns, updateAuditFilters],
  );

  const onFilterReset = useCallback(() => {
    updateAuditFilters({});
    setColumns(it =>
      it.map(column => ({
        ...column,
        filter: undefined,
        filterValue: undefined,
      })),
    );
  }, [updateAuditFilters]);

  const onColumnVisibilityChanged = useCallback(
    (columnId: string, value: boolean) => {
      setColumns(it =>
        it.map(column => {
          if (column.dataId === columnId) {
            return { ...column, hidden: !value };
          }
          return column;
        }),
      );
    },
    [],
  );

  const rowOptionsMenu = useCallback(
    (item: APIAudit, onRowMenuClose: () => void): React.ReactElement[] => [
      <MenuItem
        key="details"
        onClick={() => {
          setSelectedAudit(item);
          onRowMenuClose();
        }}
      >
        <InfoCircle
          variant="TwoTone"
          color={palette.primary900}
          size="24"
          style={{ paddingRight: '0.5rem' }}
        />
        {t('auditList.detail.viewDetails')}
      </MenuItem>,
    ],
    [t],
  );

  const onColumnHeaderClicked = useCallback(
    (columnDataId: string | number | symbol) => {
      const existingSortOrder = auditFilters.searchSortOrders?.find(
        order => order.sortField === columnDataId,
      );

      const newSortOrder =
        existingSortOrder && existingSortOrder.sortOrder === APISortOrder.DESC
          ? APISortOrder.ASC
          : APISortOrder.DESC;

      const newSort = {
        sortField: columnDataId as string,
        sortOrder: newSortOrder,
      };

      saveInStorage(PersisTableSort.Audits, newSort);

      updateAuditFilters({
        ...auditFilters,
        searchSortOrders: [newSort],
      });
    },
    [auditFilters, updateAuditFilters],
  );

  return (
    <>
      <Table<APIAudit>
        draggable
        header={t('auditList.header')}
        subHeader={t('auditList.subHeader')}
        headerIcon={
          <DocumentText
            variant="TwoTone"
            color={palette.primary900}
            size="28"
          />
        }
        onSaveTableSettings={settings => {
          saveInStorage(PersisTables.Audits, settings);
        }}
        onResetTableSettings={() => {
          setColumns(auditColumns(timezoneValue));
          updateAuditFilters({ searchSortOrders: [DEFAULT_AUDIT_SORT] });
          removeFromStorage(PersisTables.Audits);
          removeFromStorage(PersisTableSort.Audits);
        }}
        onColumnSizeChanged={(it, width) => {
          setColumns(prevColumns =>
            prevColumns.map(column =>
              column.dataId === it ? { ...column, width: width } : column,
            ),
          );
        }}
        onColumnOrderChanged={setColumns}
        filterable
        filteringOn={isFilteringOn}
        onFilteringToggle={value => {
          setIsFilteringOn(value);
          saveInStorage(PersisTableFilters.Audits, value);
        }}
        allItemsLoaded={allAuditLoaded}
        infiniteScroll
        tableMinHeight="20rem"
        columns={columns}
        items={items || []}
        sortField={
          auditFilters.searchSortOrders?.[0]?.sortField as keyof APIAudit
        }
        sortOrder={auditFilters.searchSortOrders?.[0]?.sortOrder}
        rowOptionsMenu={rowOptionsMenu}
        onRowClick={setSelectedAudit}
        onColumnHeaderClicked={onColumnHeaderClicked}
        onColumnFilterChange={onColumnFilterChange}
        onAdvancedFilterChange={onAdvancedFilterChange}
        onColumnVisibilityChanged={onColumnVisibilityChanged}
        loadItemsPending={loadAuditsPending}
        loadMoreItemsPending={loadMoreAuditsPending}
        onLoadMoreClicked={loadMoreAudits}
        onFilterReset={onFilterReset}
        bodyRowHeight="1rem"
        headerRowHeight="0.2rem"
        itemsLoaded={auditsListLoaded}
        emptyText={t('table.empty')}
        loadMoreButtonText={t('table.loadMore')}
      />
      {selectedAudit && (
        <AuditDetailModal
          audit={selectedAudit}
          onClose={() => setSelectedAudit(null)}
        />
      )}
    </>
  );
};
